import lodashGet from 'lodash/get';
import { transformNewContact2EntityContact, transformNewContact2EntityContactItem } from './transform/enterprise_new';
import { util, wait } from '@/api/util';
import { FolderTreeEditState } from '@/api/logical/mail';
import { mailBoxOfRdFlag, mailBoxOfCustomerUnFollow, mailBoxOfUnFollow, mailBoxOfDefault } from '../mail/mail_action_store_model';
import { inWindow } from '@/config';
export class ContactServerCommon {
    constructor(ctx) {
        this.syncSource = 'server_sync_source';
        this.lastSyncTimeKeyMap = {
            enterprise: 'server_sync_enterprise_last_time_',
            client: 'client_sync_last_time_',
            personal: 'server_sync_personal_last_time_',
            org: 'server_sync_org_last_time_',
            personalOrg: 'server_sync_personal_org_last_time_',
        };
        this.contactServerUrlMap = {
            enterpriseNew: 'getFullEnterpriseContactList',
            enterprise: 'getEnterpriseContactList',
            personal: 'getPersonContactList',
            org: 'getOrgList',
            personalOrg: 'getPersonalOrgList',
        };
        this.ctx = ctx;
    }
    normalizeEmailList(emailList) {
        if (!Array.isArray(emailList)) {
            return emailList;
        }
        return emailList.map(email => (typeof email === 'string' ? email.toLocaleLowerCase() : email));
    }
    getDomain(email) {
        const user = this.ctx.systemApi.getCurrentUser(email);
        return user === null || user === void 0 ? void 0 : user.domain;
    }
    resolveDomain(enterpriseId, _account) {
        var _a;
        try {
            const user = this.ctx.systemApi.getCurrentUser(_account);
            const mainDomain = (user === null || user === void 0 ? void 0 : user.domain) || '';
            const mainCompanyId = this.ctx.systemApi.getCurrentCompanyId(_account);
            if (enterpriseId === mainCompanyId) {
                return mainDomain;
            }
            const domainShareMap = lodashGet(user, 'prop.domainShareList', undefined);
            if (domainShareMap && typeof domainShareMap === 'object') {
                const subDomain = domainShareMap[String(enterpriseId)];
                if (typeof subDomain === 'string' && subDomain.length > 0) {
                    return subDomain;
                }
            }
            return mainDomain;
        }
        catch (_b) {
            return ((_a = this.ctx.systemApi.getCurrentUser(_account)) === null || _a === void 0 ? void 0 : _a.domain) || '';
        }
    }
    getAllDomains(_account) {
        const user = this.ctx.systemApi.getCurrentUser(_account);
        const mainDomain = (user === null || user === void 0 ? void 0 : user.domain) || '';
        const domainShareMap = lodashGet(user, 'prop.domainShareList', undefined);
        const domainSet = new Set();
        if (mainDomain) {
            domainSet.add(mainDomain);
        }
        if (domainShareMap && typeof domainShareMap === 'object') {
            Object.values(domainShareMap).forEach(d => {
                if (typeof d === 'string' && d.length > 0) {
                    domainSet.add(d);
                }
            });
        }
        return domainSet.size > 0 ? [...domainSet] : mainDomain ? [mainDomain] : [];
    }
    getEmail(email) {
        const user = this.ctx.systemApi.getCurrentUser(email);
        return user === null || user === void 0 ? void 0 : user.id;
    }
    getStoreData(key) {
        const { data, suc } = this.ctx.store.getSync(key);
        if (suc && data) {
            return data;
        }
        return '';
    }
    async getStoreSource() {
        const { suc, data } = await this.ctx.store.get(this.syncSource);
        const res = suc && data;
        return res || 'unknown';
    }
    async getStoredLastTime(storeInfoInDB, from) {
        let lastUpdateTime = 0;
        if (lodashGet(storeInfoInDB, 'lastUpdateTime', 0) !== 0) {
            lastUpdateTime = storeInfoInDB.lastUpdateTime;
        }
        else {
            const oldUpdateTime = await this.ctx.contactUtil.getStoreLastTime(from);
            lastUpdateTime = typeof oldUpdateTime === 'number' && oldUpdateTime > 0 ? oldUpdateTime : 0;
        }
        return lastUpdateTime;
    }
    async getSyncServerParams(options) {
        const { force = false, subDomain, from, isMainAccount, _account } = options;
        const email = this.getEmail(_account);
        const domain = typeof subDomain === 'string' && subDomain.length > 0 ? subDomain : this.getDomain(_account);
        switch (from) {
            case 'enterprise': {
                return {
                    domain,
                    source: 'unknown',
                    last_update_time: 0,
                    page_index: 1,
                    pageIndex: 1,
                };
            }
            case 'org': {
                const lastSyncRecord = await this.ctx.contactUtil.getStoreContactSyncPageInfo({
                    from,
                    domain: domain,
                    isMainAccount,
                    _account,
                });
                const { pageIndex } = lastSyncRecord;
                let lastUpdateTime = 0;
                if (!force) {
                    lastUpdateTime = await this.getStoredLastTime(lastSyncRecord, from);
                }
                const page_index = typeof pageIndex === 'number' && Number.isSafeInteger(pageIndex) ? pageIndex + 1 : 1;
                const source = force ? 'unknown' : await this.getStoreSource();
                return {
                    domain,
                    lastUpdateTime,
                    fetch_mode: 'increment',
                    source,
                    page_index,
                };
            }
            default: {
                const lastSyncRecord = await this.ctx.contactUtil.getStoreContactSyncPageInfo({
                    from,
                    domain: domain,
                    isMainAccount,
                    _account,
                });
                let lastUpdateTime = 0;
                if (!force) {
                    lastUpdateTime = await this.getStoredLastTime(lastSyncRecord, from);
                }
                const data = {
                    personal: {
                        email,
                        lastUpdateTime,
                    },
                    personalOrg: {
                        email,
                        lastUpdateTime,
                    },
                };
                return data[from];
            }
        }
    }
    async excuteContactServerWithRetry(options, config) {
        const { url, params } = options;
        const maxRetryTimes = lodashGet(config, 'maxRetryTimes', 3);
        const requestTimeoutDuration = lodashGet(config, 'requestTimeoutDuration', 2 * 60 * 1000);
        const retryDuration = lodashGet(config, 'retryDuration', 1000);
        const correctCodes = lodashGet(config, 'correctCodes', [0, 107000]);
        if (inWindow() && window.isBridgeWorker && !this.ctx.contactDB.getContactWritePriority()) {
            throw new Error('coreContactRequesting');
        }
        const retryFunc = async () => {
            var _a;
            let res;
            try {
                res = (await this.ctx.http.get(url, params, {
                    timeout: requestTimeoutDuration,
                    _account: options._account,
                }));
            }
            catch (ex) {
                this.ctx.loggerApi.track('pc_contact_sync_server_error', {
                    _account: options._account,
                    from: url,
                    code: lodashGet(ex, 'code', 'networkError'),
                    msg: lodashGet(ex, 'message', 'networkError'),
                    domain: this.getDomain(options._account),
                    enableTrackInBg: true,
                });
                throw ex instanceof Error ? ex : new Error(`${ex}`);
            }
            const serverCode = lodashGet(res, 'data.data.statusCode', -1);
            if (correctCodes.includes(serverCode) && ((_a = res.data) === null || _a === void 0 ? void 0 : _a.data) && (typeof (config === null || config === void 0 ? void 0 : config.validateRes) !== 'function' || config.validateRes(res.data.data))) {
                return res.data.data;
            }
            this.ctx.dataTrackerApi.track('pc_contact_sync_server_error', {
                _account: options._account,
                from: url,
                code: serverCode,
                enableTrackInBg: true,
                domain: this.getDomain(options._account),
            });
            throw new Error(lodashGet(res, 'data.data.message', '') + '(' + serverCode + ')');
        };
        let request = retryFunc();
        let retryTimes = 1;
        while (retryTimes < maxRetryTimes) {
            retryTimes += 1;
            request = request.catch(async (err) => {
                if (lodashGet(err, 'message', '').indexOf('106000') !== -1) {
                    throw err;
                }
                await wait(retryDuration);
                return retryFunc();
            });
        }
        return request;
    }
    async retry(fn, count = 3) {
        try {
            const data = await fn();
            return {
                success: true,
                data,
            };
        }
        catch (e) {
            if (count - 1 <= 0) {
                return {
                    success: false,
                    error: e,
                };
            }
            return this.retry(fn, count - 1);
        }
    }
    syncListAliasName(from) {
        const data = {
            personal: 'personContactVOList',
            enterprise: 'contactVOList',
            org: 'unitVOList',
            personalOrg: 'personContactGroupList',
        };
        return data[from];
    }
    getServerUrl(from, params) {
        const needNewEnterpriseUrl = from === 'enterprise' && lodashGet(params, 'force', false) === true;
        return needNewEnterpriseUrl ? this.contactServerUrlMap.enterpriseNew : this.contactServerUrlMap[from];
    }
    async searchContactByServer(query) {
        var _a, _b;
        const $url = this.ctx.systemApi.getUrl('searchContact');
        const defaultDomain = ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.domain) || '';
        try {
            const { data } = await this.ctx.http.get($url, Object.assign({ limit: 200, domain: defaultDomain }, query));
            return ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.contactVOList) || [];
        }
        catch (e) {
            console.warn('[contact_server] get server contact', e);
            return Promise.resolve([]);
        }
    }
    async getContactsByUnitId(query) {
        var _a, _b;
        const $url = this.ctx.systemApi.getUrl('getContactByUnitId');
        const defaultDomain = ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.domain) || '';
        const params = Object.assign({ domain: defaultDomain, pageSize: 100 }, query);
        try {
            const { data } = await this.ctx.http.get($url, params, { _account: query._account });
            return ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.contactVOList) || [];
        }
        catch (e) {
            console.warn('[contact_server] get server contact', e);
            return Promise.resolve([]);
        }
    }
    async getContactByQiyeAccountId(query) {
        var _a, _b;
        const idList = (query === null || query === void 0 ? void 0 : query.idList.filter(Boolean)) || [];
        if (!idList.length) {
            return [];
        }
        const _account = query._account || this.ctx.contactUtil.getCurrentAccount();
        const $url = this.ctx.systemApi.getUrl('getContactByQiyeAccountId');
        const defaultDomain = ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.domain) || '';
        const params = {
            domain: defaultDomain,
            qiyeAccountIdList: idList,
        };
        try {
            const { data } = await this.ctx.http.post($url, params, {
                _account,
                contentType: 'json',
            });
            const itemList = ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.contactVOList) || [];
            return this.transServer2ContactModel({
                list: itemList,
                _account,
            });
        }
        catch (e) {
            console.warn('[contact_server] get server contact', e);
            return [];
        }
    }
    async doSearch(condition) {
        const { query, showDisable, contactType, isIM, showNotDisplayEmail } = condition;
        const list = await this.searchContactByServer({ keyword: query });
        const modelList = this.ctx.contactTrans.transServerSearch(list);
        const filterContactModelList = [];
        modelList.forEach(item => {
            const imFlag = !isIM || (isIM && item.enableIM);
            const visibleFlag = !showDisable || (showDisable && item.visibleCode === 0);
            const typeFlag = !contactType || (contactType && item.type === contactType);
            const diplayEmailFlag = !showNotDisplayEmail || (showNotDisplayEmail && (item.visibleCode === 7 || item.visibleCode === 0));
            if (imFlag && visibleFlag && typeFlag && diplayEmailFlag) {
                filterContactModelList.push(item);
            }
        });
        return filterContactModelList;
    }
    async doSearchAllContact(condition) {
        const { query, showDisable, isIM } = condition;
        const list = await this.searchContactByServer({ keyword: query });
        const modelList = this.transServer2ContactModel({
            list,
            _account: condition._account,
        });
        const filterContactModelList = [];
        modelList.forEach(item => {
            const imFlag = !isIM || (isIM && item.contact.enableIM);
            const visibleFlag = !showDisable || (showDisable && item.contact.visibleCode === 0);
            if (imFlag && visibleFlag) {
                filterContactModelList.push(item);
            }
        });
        return filterContactModelList;
    }
    async getContactByYunxin(accounts, _account) {
        var _a;
        if (!accounts || !accounts.length) {
            return [];
        }
        try {
            const yunxinAccIdList = accounts.filter(item => !!item).join(',');
            const { data } = await this.ctx.http.post(this.ctx.systemApi.getUrl('getContactByYunxin'), { yunxinAccIdList });
            return (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.itemList;
        }
        catch (e) {
            console.error('[contact_server] getContactByYunxin error', e, _account);
            return [];
        }
    }
    async doGetContactByYunxin(accounts, _account) {
        const itemList = await this.getContactByYunxin(accounts, _account);
        if (itemList === null || itemList === void 0 ? void 0 : itemList.length) {
            return this.transServer2ContactModel({ list: itemList });
        }
        return [];
    }
    transServer2ContactModel(params) {
        const { list: itemList, _account } = params;
        if (!(itemList === null || itemList === void 0 ? void 0 : itemList.length)) {
            return [];
        }
        const contactList = this.ctx.contactTrans.transEnterpriseListToContact(itemList, Date.now(), {
            _account,
        });
        const contactInfoList = this.ctx.contactTrans.transEnterpriseListToContactItem(itemList);
        return this.ctx.contactTrans.transformContactModel({
            contactList,
            contactInfoList,
        });
    }
    async getEnterpriseContactDetail(params) {
        var _a, _b, _c;
        const { queryType, queryValues, enterpriseId, lastUpdateTime, domain, _account } = params;
        if (!queryValues.length) {
            return { contacts: [], contactItems: [] };
        }
        try {
            const url = this.ctx.systemApi.getUrl('getEnterpriseContactDetail');
            const body = queryType === 'accountId' ? { accountId: queryValues, domain } : { email: queryValues, domain };
            const res = await this.ctx.http.post(url, body, { _account, contentType: 'json' });
            const raw = (_c = (_b = (_a = res) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.contact;
            const rawList = raw ? [raw] : [];
            const contacts = [];
            const contactItems = [];
            rawList.forEach((item) => {
                contacts.push(transformNewContact2EntityContact(item, { enterpriseId, lastUpdateTime }));
                transformNewContact2EntityContactItem(item, { enterpriseId, lastUpdateTime }).forEach(ci => contactItems.push(ci));
            });
            return { contacts, contactItems };
        }
        catch (e) {
            console.warn('[contact_server] getEnterpriseContactDetail error', e);
            return { contacts: [], contactItems: [] };
        }
    }
    async getEnterpriseContactBase(params) {
        var _a, _b, _c;
        const { queryType, queryValues, enterpriseId, lastUpdateTime, domain, _account } = params;
        if (!queryValues.length) {
            return { contacts: [], contactItems: [] };
        }
        try {
            const url = this.ctx.systemApi.getUrl('getEnterpriseContactBase');
            const body = queryType === 'accountId' ? { accountIds: queryValues, domain } : { emails: queryValues, domain };
            const res = await this.ctx.http.post(url, body, { _account, contentType: 'json' });
            const rawList = ((_c = (_b = (_a = res) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.contacts) || [];
            const contacts = [];
            const contactItems = [];
            rawList.forEach((item) => {
                contacts.push(transformNewContact2EntityContact(item, { enterpriseId, lastUpdateTime }));
                transformNewContact2EntityContactItem(item, { enterpriseId, lastUpdateTime }).forEach(ci => contactItems.push(ci));
            });
            return { contacts, contactItems };
        }
        catch (e) {
            console.warn('[contact_server] getEnterpriseContactBase error', e);
            return { contacts: [], contactItems: [] };
        }
    }
    async doGetContactByEmails(emails, _account, uniqueEmail) {
        var _a;
        try {
            const emailSet = new Set();
            emails.forEach(item => {
                if (item) {
                    emailSet.add(item);
                }
            });
            const emailList = [...emailSet].join(',');
            const domain = this.getDomain();
            const { data } = await this.ctx.http.post(this.ctx.systemApi.getUrl('getYunxinInfoByEmail'), { emailList, domain }, { _account });
            const itemList = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.itemList;
            if (itemList === null || itemList === void 0 ? void 0 : itemList.length) {
                const modelList = this.transServer2ContactModel({
                    list: itemList,
                    _account,
                });
                if (uniqueEmail) {
                    return this.ctx.contactTrans.uniqueServerContactListByEmails([...emailSet], modelList);
                }
                return modelList;
            }
        }
        catch (e) {
            console.error('[contact_server] doGetContactByEmails error', e);
        }
        console.log(emails, _account);
        return [];
    }
    async doGetContactByOrgId(params) {
        const { orgId, showDisable, _account } = params;
        const orgIds = util.singleToList(orgId);
        const emailMap = {};
        const orgMap = {};
        const promiseList = orgIds.map(id => this.getContactsByUnitId({ unitId: id, _account }).then(itemList => {
            const modelList = this.transServer2ContactModel({
                list: itemList,
                _account: params._account,
            });
            const orgEmailList = orgMap[id] || [];
            modelList.forEach(item => {
                const email = item.contact.displayEmail || item.contact.accountName;
                if (showDisable || (!showDisable && item.contact.visibleCode === 0)) {
                    emailMap[email] = item;
                    orgEmailList.push(item);
                }
            });
            orgMap[id] = orgEmailList;
        }));
        await Promise.all(promiseList);
        return {
            orgMap,
            uniqueList: Object.values(emailMap),
        };
    }
    async getOrgDetailByServer(lastUpdateTime) {
        var _a, _b;
        const { data } = await this.ctx.http.get(this.ctx.systemApi.getUrl('getOrgList'), {
            lastUpdateTime,
            domain: this.getDomain(),
            isAll: 1,
            fetch_mode: 'increment',
        }, {
            _account: ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
        });
        return ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.unitVOList) || [];
    }
    async getContactByServer(lastUpdateTime) {
        var _a, _b;
        try {
            const { data } = await this.ctx.http.get(this.ctx.systemApi.getUrl('getEnterpriseContactList'), {
                lastUpdateTime,
                domain: this.getDomain(),
                isAll: 1,
                fetch_mode: 'increment',
            }, {
                _account: ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
            });
            return ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.contactVOList) || [];
        }
        catch (e) {
            console.warn('[contact] get server contact', e);
            return Promise.resolve([]);
        }
    }
    async getRecentContactListInServer(params) {
        var _a, _b, _c, _d;
        try {
            const { data } = await this.ctx.http.get(this.ctx.systemApi.getUrl('getRecentContactList'), params, {
                _account: params._account || ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
            });
            if ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.contactList) {
                this.recentContactDiffs((_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.contactList, params);
            }
            return ((_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.contactList) || [];
        }
        catch (e) {
            return [];
        }
    }
    async recentContactDiffs(contactList, params) {
        const dbContactList = await this.ctx.contactDB.getRecentContact(params);
        if (!dbContactList || contactList.length !== dbContactList.length) {
            this.ctx.contactDB.saveRecentContactInDb(contactList, params);
            return;
        }
        const dbContactEmailStr = dbContactList.reduce((prev, item) => prev + item.email, '');
        const contactEmailStr = contactList.reduce((prev, item) => prev + item.email, '');
        if (dbContactEmailStr !== contactEmailStr) {
            this.ctx.contactDB.saveRecentContactInDb(contactList, params);
        }
    }
    mappingToMailBoxEntry(data) {
        const dt = {};
        data.forEach((item, index) => {
            if (item) {
                const { id, belongEmail = 'main' } = item;
                const list = dt[belongEmail] || [];
                const stats = item.stats || {};
                const flags = item.flags || {};
                const entry = {
                    mailBoxName: item.name,
                    mailBoxCurrentUnread: stats.unreadMessageCount || 0,
                    mailBoxUnread: stats.unreadMessageCount || 0,
                    mailBoxTotal: stats.messageCount || 0,
                    mailBoxType: flags.system ? 'sys' : 'customer',
                    mailBoxId: id,
                    threadMailBoxCurrentUnread: Math.max(stats.unreadThreadCount || 0, 0),
                    threadMailBoxUnread: Math.max(stats.unreadThreadCount || 0, 0),
                    threadMailBoxTotal: Math.max(stats.threadCount || 0, 0),
                    mailBoxParent: item.parent,
                    id: '' + id,
                    pid: item.parent,
                    sort: index,
                    _state: FolderTreeEditState.DEFAULT,
                    _deep: 0,
                    _isTempNode: false,
                };
                list.push(entry);
                dt[belongEmail] = list;
            }
        });
        return dt;
    }
    handleEntityMailBox2MailBoxModel(_dt) {
        var _a, _b;
        if (!Array.isArray(_dt) || _dt.length === 0) {
            return { mailBoxModel: [], ids: [] };
        }
        const ids = _dt.map(v => v.mailBoxId + '');
        const ret = [];
        const trans = {};
        const baseExtraMailBoxes = [mailBoxOfRdFlag];
        if (process.env.BUILD_ISEDM || ((_a = this.ctx.systemApi.doGetProductInfo()) === null || _a === void 0 ? void 0 : _a.navigateVersion)) {
            baseExtraMailBoxes.push(mailBoxOfCustomerUnFollow);
            baseExtraMailBoxes.push(mailBoxOfUnFollow);
        }
        const systemMailBoxes = _dt.filter(v => v.mailBoxType === 'sys').map(b => b.mailBoxId);
        const defaultFolderSort = ((_b = _dt.find(v => v.mailBoxId === mailBoxOfDefault.id)) === null || _b === void 0 ? void 0 : _b.sort) || 0;
        baseExtraMailBoxes.forEach(it => {
            if (!systemMailBoxes.includes(it.id)) {
                let sort = it.id;
                if (it.id === mailBoxOfCustomerUnFollow.id) {
                    sort = defaultFolderSort + 0.1;
                }
                else if (it.id === mailBoxOfUnFollow.id) {
                    sort = defaultFolderSort + 0.2;
                }
                const mailBox = {
                    childrenCount: 0,
                    entry: {
                        mailBoxName: it.name || '',
                        mailBoxCurrentUnread: 0,
                        mailBoxUnread: 0,
                        mailBoxTotal: 0,
                        mailBoxType: 'sys',
                        mailBoxId: it.id,
                        mailBoxParent: 0,
                        threadMailBoxTotal: 0,
                        threadMailBoxUnread: 0,
                        threadMailBoxCurrentUnread: 0,
                        id: it.id + '',
                        sort,
                    },
                    mailBoxId: it.id,
                    children: [],
                };
                ret.push(mailBox);
                trans[it.id] = mailBox;
            }
        });
        for (const entry of _dt) {
            const id = Number(entry.mailBoxId);
            trans[id] = {
                childrenCount: 0,
                entry,
                children: [],
            };
        }
        for (const item of _dt) {
            const id = item.mailBoxId;
            const pid = item.pid || 0;
            const model = trans[+id];
            model.mailBoxId = id;
            if (pid === 0) {
                ret.push(model);
            }
            else {
                const parent = trans[pid];
                if (parent) {
                    parent.children.push(model);
                    parent.childrenCount += 1;
                }
            }
        }
        ret.sort((a, b) => a.entry.sort - b.entry.sort);
        ret.unshift({
            childrenCount: 0,
            entry: {
                mailBoxName: '全部邮件' || '',
                mailBoxCurrentUnread: 0,
                mailBoxUnread: 0,
                mailBoxTotal: 0,
                mailBoxType: 'sys',
                mailBoxId: 'ALL_FOLDER',
                mailBoxParent: 0,
                threadMailBoxTotal: 0,
                threadMailBoxUnread: 0,
                threadMailBoxCurrentUnread: 0,
                id: 'ALL_FOLDER',
                sort: -2,
            },
            mailBoxId: 'ALL_FOLDER',
            children: [],
        });
        return { mailBoxModel: ret, ids };
    }
    async searchEnterpriseContacts(keyword, domain, limit = 100) {
        var _a;
        const url = this.ctx.systemApi.getUrl('searchEnterpriseContacts');
        try {
            const { data } = await this.ctx.http.get(url, { keyword, domain, limit });
            return { contacts: ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.contacts) || [] };
        }
        catch (e) {
            console.warn('[contact_server] searchEnterpriseContacts failed', e);
            return { contacts: [] };
        }
    }
}
